import React, { useState, useEffect } from 'react'
import { Alert, Modal, Spinner } from 'react-bootstrap'
import { useTheme } from '../../context/ThemeContext';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { prepareEnvelope, sendEnvelope } from '../../services/envelopes';
import { getAuth } from '../../modules/auth/core/AuthHelpers';
import { setIntegrations } from '../../store/slice/integration-slice';
import { useDispatch } from 'react-redux';

interface SendEnvelopeModalProps {
    showModal: boolean;
    closeModal: () => void;
    setEnvelope?: any;
    assignees?: any[];
    pageId?: string;
    cardId?: string;
    refetchPageEnvelopes?: (pageId: string) => void;
    refetchCardEnvelopes?: (cardId: string) => void;
}
interface SigningFormValues {
    documentFiles: File[];
    documentNames: string[];
    signerEmails: string[];
    signerNames: string[];
    message: string;
    subject: string;
    expirationDays: number;
}
const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/png',
    'image/jpeg'
];

const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.docx', '.doc', '.xlsx', '.png', '.jpg', '.jpeg'];

const validationSchema = Yup.object().shape({
    documentFiles: Yup.array()
        .of(Yup.mixed<File>())
        .min(1, 'At least one document is required')
        .max(5, 'Maximum 5 documents allowed')
        .required('Documents are required')
        .test('fileTypes', 'Only PDF, TXT, DOCX, DOC, XLSX, PNG, JPG, and JPEG files are supported', (files) => {
            if (!files || files.length === 0) return false;
            return files.every((file: any) => {
                const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
                return ALLOWED_FILE_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(extension);
            });
        })
        .test('fileSizes', 'Each file must be less than 5MB', (files) => {
            if (!files) return true;
            return files.every((file: any) => file.size <= 5 * 1024 * 1024);
        }),
    documentNames: Yup.array()
        .of(Yup.string().required('Document name is required'))
        .min(1, 'At least one document name is required')
        .required('Document names are required'),
    signerEmails: Yup.array()
        .of(
            Yup.string()
                .required('Email is required')
                .email('Invalid email address')
        )
        .min(1, 'At least one signer is required')
        .required('Signer emails are required'),

    signerNames: Yup.array().of(Yup.string()),
    message: Yup.string()
        .max(1000, 'Message cannot be longer than 1000 characters'),
    subject: Yup.string().max(255, 'Subject cannot be longer than 255 characters'),
    expirationDays: Yup.number()
        .min(1, 'Expiration must be at least 1 day')
        .max(365, 'Expiration cannot exceed 365 days')
});


const SendEnvelopeModal: React.FC<SendEnvelopeModalProps> = ({ showModal, closeModal, setEnvelope, assignees = [], pageId, cardId, refetchPageEnvelopes, refetchCardEnvelopes }) => {
    const { theme } = useTheme()
    const [disableBtn, setDisableBtn] = useState(false)
    const [confirmMsg, setConfirmMsg] = useState('')
    const [confirmMsgError, setConfirmMsgError] = useState('')
    const [isDragging, setIsDragging] = useState(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const dispatch = useDispatch();

    // Pre-populate signers from assignees if available
    const getInitialSigners = () => {
        if (assignees && assignees.length > 0) {
            const auth = getAuth();
            const currentUserEmail = (auth?.email || auth?.data?.user?.email || '').toLowerCase();
            // Filter out the current logged-in user
            const filteredAssignees = assignees.filter((assignee: any) => {
                const assigneeEmail = (assignee.email || assignee.value || '').toLowerCase();
                return assigneeEmail && assigneeEmail !== currentUserEmail;
            });
            const signerEmails = filteredAssignees.map((assignee: any) => assignee.email || assignee.value || '').filter(Boolean);
            const signerNames = filteredAssignees.map((assignee: any) => assignee.label || assignee.name || '').filter(Boolean);
            return { signerEmails, signerNames };
        }
        return { signerEmails: [], signerNames: [] };
    };

    const { signerEmails: initialSignerEmails, signerNames: initialSignerNames } = getInitialSigners();

    const initialValues: SigningFormValues = {
        documentFiles: [],
        documentNames: [],
        signerEmails: initialSignerEmails,
        signerNames: initialSignerNames,
        message: '',
        subject: '',
        expirationDays: 15
    };

    const handleFileSelection = async (files: FileList | File[] | null) => {
        if (!files || files.length === 0) {
            return;
        }

        const filesArray = Array.from(files);
        const currentFiles = formik.values.documentFiles;

        // Check if adding these files would exceed the limit
        if (currentFiles.length + filesArray.length > 5) {
            await formik.setFieldTouched('documentFiles', true, false);
            await formik.setFieldError('documentFiles', `Maximum 5 documents allowed. You can only add ${5 - currentFiles.length} file(s).`);
            return;
        }

        // Validate each file
        const invalidFiles = filesArray.filter(file => {
            const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
            return !ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(extension);
        });
        if (invalidFiles.length > 0) {
            await formik.setFieldTouched('documentFiles', true, false);
            await formik.setFieldError('documentFiles', 'Only PDF, TXT, DOCX, DOC, XLSX, PNG, JPG, and JPEG files are supported');
            return;
        }

        const oversizedFiles = filesArray.filter(file => file.size > 5 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            await formik.setFieldTouched('documentFiles', true, false);
            await formik.setFieldError('documentFiles', 'Each file must be less than 5MB');
            return;
        }

        // Clear any previous errors before adding valid files
        await formik.setFieldError('documentFiles', undefined);

        // Add valid files
        const updatedFiles = [...currentFiles, ...filesArray];
        await formik.setFieldValue('documentFiles', updatedFiles);
        formik.setFieldTouched('documentFiles', true);
        await formik.validateField('documentFiles');

        // Generate document names for each file
        const newDocumentNames = filesArray?.map((file) => {
            const lastDotIndex = file.name.lastIndexOf('.');
            return file.name.slice(0, lastDotIndex);
        });

        const updatedDocumentNames = [...formik.values.documentNames, ...newDocumentNames];
        await formik.setFieldValue('documentNames', updatedDocumentNames);
    };

    const handleRemoveFile = async (index: number) => {
        const updatedFiles = formik.values.documentFiles.filter((_, i) => i !== index);
        const updatedDocumentNames = formik.values.documentNames.filter((_, i) => i !== index);

        await formik.setFieldValue('documentFiles', updatedFiles);
        await formik.setFieldValue('documentNames', updatedDocumentNames);
        await formik.validateField('documentFiles');
        await formik.validateField('documentNames');
    };

    const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;

        if (files && files.length > 0) {
            await handleFileSelection(files);
        }
    };

    // Helper function to build FormData from form values
    const buildFormData = (values: SigningFormValues): FormData => {
        const formData = new FormData();

        if (values.documentFiles && values.documentFiles.length > 0) {
            values.documentFiles.forEach((file, index) => {
                formData.append(`documentFiles[${index}]`, file);
            });
        }

        if (values.documentNames && values.documentNames.length > 0) {
            values.documentNames.forEach((name, index) => {
                formData.append(`documentNames[${index}]`, name);
            });
        }

        (['message', 'subject'] as (keyof SigningFormValues)[]).forEach((field) => {
            const value = values[field];
            if (typeof value === 'string' && value.length > 0) {
                formData.append(field, value);
            }
        });

        formData.append('expirationDays', values.expirationDays.toString());

        values.signerEmails.forEach((email, index) => {
            formData.append(`signerEmails[${index}]`, email);
        });

        values.signerNames.forEach((name, index) => {
            formData.append(`signerNames[${index}]`, name);
        });

        if (pageId) {
            formData.append('pageId', pageId);
        }

        if (cardId) {
            formData.append('cardId', cardId);
        }

        return formData;
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            // Just show the confirmation modal without calling any API yet
            setShowConfirmModal(true);
            setSubmitting(false);
        }
    });

    // Reset form with assignee data when modal opens
    useEffect(() => {
        if (showModal && assignees && assignees.length > 0) {
            const auth = getAuth();
            const currentUserEmail = (auth?.email || auth?.data?.user?.email || '').toLowerCase();
            // Filter out the current logged-in user
            const filteredAssignees = assignees.filter((assignee: any) => {
                const assigneeEmail = (assignee.email || assignee.value || '').toLowerCase();
                return assigneeEmail && assigneeEmail !== currentUserEmail;
            });
            const signerEmails = filteredAssignees.map((assignee: any) => assignee.email || assignee.value || '').filter(Boolean);
            const signerNames = filteredAssignees.map((assignee: any) => assignee.label || assignee.name || '').filter(Boolean);

            formik.setFieldValue('signerEmails', signerEmails);
            formik.setFieldValue('signerNames', signerNames);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, assignees]);

    // Handler for "Continue" button - User wants to add custom fields
    const handleConfirmRedirect = async () => {
        try {
            setDisableBtn(true);

            const formData: any = buildFormData(formik.values);

            const res = await prepareEnvelope(formData);

            if (res.data.success) {
                const url = res.data.data?.embeddedUrl;
                if (url) {
                    setConfirmMsg(res.data.message);

                    // Refetch page envelopes if needed
                    if (pageId && refetchPageEnvelopes) {
                        refetchPageEnvelopes(pageId);
                    }

                    // Refetch card envelopes if needed
                    if (cardId && refetchCardEnvelopes) {
                        refetchCardEnvelopes(cardId);
                    }

                    // Open URL in new tab
                    window.open(url, '_blank', 'noopener,noreferrer');

                    // Close modals and reset form after a brief delay
                    setTimeout(() => {
                        setShowConfirmModal(false);
                        closeModal();
                        formik.resetForm();
                        setConfirmMsg('');
                        setDisableBtn(false);
                    }, 1000);
                }
            }
        } catch (error: any) {
            setConfirmMsgError(error?.response?.data?.message || 'Failed to prepare envelope');
            setDisableBtn(false);
            setTimeout(() => {
                setConfirmMsgError('');
                if (error?.response?.data?.message === 'You do not have access to the DocuSign integration for this organization.') {
                    dispatch(setIntegrations([]));
                }
                setShowConfirmModal(false);
                closeModal();
                formik.resetForm();
            }, 3000);
        }
    };

    // Handler for "Send it anyway" button - User doesn't want custom fields
    const handleSendWithoutCustomFields = async () => {
        try {
            setDisableBtn(true);

            const formData = buildFormData(formik.values);

            const res = await sendEnvelope(formData);

            if (res.data.success) {
                setConfirmMsg(res.data.message);

                // Update envelope state if setter is provided
                setEnvelope && setEnvelope((prev: any) => ({
                    ...prev,
                    envelopes: [res?.data?.data?.envelopeRecord, ...(prev?.envelopes || [])],
                }));

                // Refetch page envelopes if needed
                if (pageId && refetchPageEnvelopes) {
                    refetchPageEnvelopes(pageId);
                }

                // Refetch card envelopes if needed
                if (cardId && refetchCardEnvelopes) {
                    refetchCardEnvelopes(cardId);
                }

                setDisableBtn(false);

                // Close modals and reset form after showing success message
                setTimeout(() => {
                    closeModal();
                    formik.resetForm();
                    setDisableBtn(false);
                    setShowConfirmModal(false);
                    setConfirmMsg('');
                }, 3000);
            }
        } catch (error: any) {
            setConfirmMsgError(error?.response?.data?.message || 'Failed to send envelope');
            setDisableBtn(false);
            setTimeout(() => {
                setConfirmMsgError('');
                if (error?.response?.data?.message === 'You do not have access to the DocuSign integration for this organization.') {
                    dispatch(setIntegrations([]));
                    setShowConfirmModal(false);
                    closeModal();
                    formik.resetForm();
                }
            }, 3000);
        }
    };

    return (
        <>
            {/* Confirmation Modal */}
            <Modal
                contentClassName={theme === 'dark' ? 'theme__div' : ''}
                show={showConfirmModal}
                onHide={() => {
                    closeModal()
                    setShowConfirmModal(false)
                }}
                centered
            >
                <Modal.Header>
                    <Modal.Title className={`${theme === 'dark' ? 'white__text' : ''}`}>
                        Add Signature Fields
                    </Modal.Title>
                    <i className={`bi bi-x-lg modal-icon-custom ${theme === 'dark' && 'close'}`} onClick={() => {
                        closeModal()
                        setShowConfirmModal(false)
                    }}></i>
                </Modal.Header>
                <Modal.Body>
                    <p className={`${theme === 'dark' ? 'white__text' : 'text-dark'}`}>
                        Do you want to add custom fields to your document before sending it out for signature?
                    </p>
                </Modal.Body>

                <Modal.Footer>
                    {confirmMsg && (
                        <Alert variant='success' className='my-2 w-100'>
                            {confirmMsg}
                        </Alert>
                    )}
                    {confirmMsgError && (
                        <Alert variant='danger' className='my-2 w-100'>
                            {confirmMsgError}
                        </Alert>
                    )}
                    {disableBtn ? <button className='btn btn-primary' disabled>
                        <Spinner animation='border' variant='light' />
                    </button> :
                        <>
                            <button className='btn btn-secondary' onClick={handleSendWithoutCustomFields} disabled={disableBtn}>
                                Send it anyway
                            </button>
                            <button className='btn btn-primary' onClick={handleConfirmRedirect} disabled={disableBtn}>
                                Continue
                            </button></>}


                </Modal.Footer>
            </Modal>

            {/* Main Send Envelope Modal */}
            <Modal
                contentClassName={theme === 'dark' ? 'theme__div' : ''}
                show={showModal}
                // onHide={closeModal}
                centered
                size='xl'
            >
                <Modal.Header>
                    <Modal.Title className={`${theme === 'dark' ? 'white__text' : ''}`}>
                        Send Envelope
                    </Modal.Title>
                    <i className={`bi bi-x-lg modal-icon-custom ${theme === 'dark' && 'close'}`} onClick={closeModal}></i>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={formik.handleSubmit}>
                        <div className='row w-100 m-auto'>
                            <div className="col-12 mb-4">
                                <div className="card">
                                    <div className={`card-body rounded ${theme === 'dark' ? 'layout__theme__div' : 'form-control form-control-lg form-control-solid'}`}>
                                        {/* Header */}
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <label
                                                htmlFor="documentFiles"
                                                className={`form-label fs-5 fw-semibold mb-0 ${theme === 'light' ? 'text-dark' : 'text-white'}`}
                                            >
                                                Document Files <span className="text-danger">*</span>
                                            </label>

                                            {/* File count badge */}
                                            {formik.values.documentFiles.length > 0 && (
                                                <span className="badge bg-primary">
                                                    <i className="bi bi-file-earmark-pdf me-2"></i>
                                                    {formik.values.documentFiles.length} file(s) selected
                                                </span>
                                            )}
                                        </div>

                                        {/* Display uploaded files */}
                                        {formik.values.documentFiles.length > 0 && (
                                            <div className="mb-3">
                                                <div className="d-flex flex-wrap gap-2">
                                                    {formik.values.documentFiles.map((file, index) => (
                                                        <div
                                                            key={index}
                                                            className={`alert alert-dismissible fade show mb-0 d-flex align-items-center ${theme === 'dark' ? 'alert-dark' : 'alert-light'}`}
                                                            style={{ padding: '0.5rem 1rem' }}
                                                        >
                                                            <i className="bi bi-file-earmark-pdf text-danger me-2"></i>
                                                            <span className="me-2" style={{ fontSize: '0.875rem' }}>{file.name}</span>
                                                            <button
                                                                type="button"
                                                                className="btn-close my-2 py-1 px-2"
                                                                onClick={() => handleRemoveFile(index)}
                                                                aria-label="Remove file"
                                                                style={{ fontSize: '0.7rem' }}
                                                            ></button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Custom file input - only show if less than 5 files */}
                                        {formik.values.documentFiles.length < 5 && (
                                            <div className="file-input-wrapper">
                                                <input
                                                    id="documentFiles"
                                                    type="file"
                                                    accept=".pdf,.txt,.docx,.doc,.xlsx,.png,.jpg,.jpeg,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg"
                                                    multiple
                                                    className="d-none"
                                                    onChange={async (event) => {
                                                        const files = event.currentTarget.files;
                                                        await handleFileSelection(files);
                                                        event.target.value = ''; // Reset input
                                                    }}
                                                />
                                                <label
                                                    htmlFor="documentFiles"
                                                    className={`file-input-label btn btn-outline d-flex align-items-center justify-content-center p-4 cursor-pointer ${isDragging ? 'border-primary bg-light-primary' : ''}`}
                                                    style={{ minHeight: '180px' }}
                                                    onDragEnter={handleDragEnter}
                                                    onDragLeave={handleDragLeave}
                                                    onDragOver={handleDragOver}
                                                    onDrop={handleDrop}
                                                >
                                                    <div className="text-center">
                                                        <i className={`${formik.touched.documentFiles && formik.errors.documentFiles ? 'bi bi-exclamation-triangle text-warning' :
                                                            formik.values.documentFiles.length > 0 ? 'bi bi-check-circle text-success' : 'bi bi-cloud-upload'
                                                            } fs-1 mb-2`}></i>
                                                        <div className="fs-5 text-muted">
                                                            {isDragging ? 'Drop files here' :
                                                                formik.touched.documentFiles && formik.errors.documentFiles ? 'Invalid Files' :
                                                                    formik.values.documentFiles.length > 0 ? 'Add more files' : 'Click to upload or drag and drop'}
                                                        </div>
                                                        <div className={`small mt-1 ${(formik.touched.documentFiles && formik.errors.documentFiles) ? 'text-danger' : 'text-muted'
                                                            }`}>
                                                            {(formik.touched.documentFiles && typeof formik.errors.documentFiles === 'string' && formik.errors.documentFiles) || `Supported: PDF, TXT, DOCX, DOC, XLSX, PNG, JPG, JPEG (Max ${5 - formik.values.documentFiles.length} more files)`}
                                                        </div>
                                                    </div>
                                                </label>
                                            </div>
                                        )}

                                        {/* Max files reached message */}
                                        {formik.values.documentFiles.length >= 5 && (
                                            <div className="alert alert-info mt-3 d-flex align-items-center">
                                                <i className="bi bi-info-circle me-2"></i>
                                                <span>Maximum 5 documents reached. Remove a file to add more.</span>
                                            </div>
                                        )}

                                        {/* Error message */}
                                        {formik.touched.documentFiles && formik.errors.documentFiles && (
                                            <div className="alert alert-danger mt-3 d-flex align-items-center">
                                                <i className="bi bi-exclamation-circle me-2 text-danger"></i>
                                                <span>
                                                    {typeof formik.errors.documentFiles === 'string'
                                                        ? formik.errors.documentFiles
                                                        : 'Please check the files and try again'}
                                                </span>
                                            </div>
                                        )}

                                        {/* Help text */}
                                        {!formik.errors.documentFiles && (
                                            <div className={`form-text mt-2 ${theme === 'light' ? 'text-muted' : 'text-light'}`}>
                                                <i className="bi bi-info-circle me-2"></i>
                                                Maximum file size: 5MB per file. Supported formats: PDF, TXT, DOCX, DOC, XLSX, PNG, JPG, JPEG. You can upload up to 5 documents.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 my-2">
                                <div className='d-flex justify-content-between align-items-center gap-2'>
                                    <div className={`fs-3 fw-semibold ${theme === 'light' ? 'dark__text' : ''}`}>
                                        Signers <span className="text-danger">*</span>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-secondary mt-2 col-4 col-md-4"
                                        onClick={() => {
                                            formik.setFieldValue('signerEmails', [
                                                ...formik.values.signerEmails,
                                                ''
                                            ]);
                                            formik.setFieldValue('signerNames', [
                                                ...formik.values.signerNames,
                                                ''
                                            ]);
                                        }}
                                    >
                                        Add Signer
                                    </button>
                                </div>
                                {formik.values.signerEmails.map((_: string, index: number) => (
                                    <div key={index} className="signer-row mb-3">
                                        <div className="row g-3">
                                            {/* Email Field */}
                                            <div className="col-md-5 col-12">
                                                <label htmlFor={`signerEmails.${index}`} className={`fs-5 fw-semibold mt-3 ${theme === 'light' ? 'dark__text' : ''}`}>
                                                    Email {index + 1} <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    name={`signerEmails.${index}`}
                                                    className={clsx(
                                                        `form-control form-control-lg form-control-solid  ${theme === 'dark' ? 'dark__theme__input' : ''
                                                        }  italics-placeholder mt-2`,
                                                        {
                                                            'is-invalid': Array.isArray(formik.touched.signerEmails) && Array.isArray(formik.errors.signerEmails) && !!formik.touched.signerEmails?.[index] && !!formik.errors.signerEmails?.[index],
                                                        },
                                                        {
                                                            'is-valid': Array.isArray(formik.touched.signerEmails) && Array.isArray(formik.errors.signerEmails) && !!formik.touched.signerEmails?.[index] && !formik.errors.signerEmails?.[index],
                                                        }
                                                    )}

                                                    placeholder={`Enter Signer Email`}
                                                    value={formik.values.signerEmails[index]}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                                {Array.isArray(formik.touched.signerEmails) &&
                                                    !!formik.touched.signerEmails[index] &&
                                                    Array.isArray(formik.errors.signerEmails) &&
                                                    !!formik.errors.signerEmails[index] && (
                                                        <div className='fv-plugins-message-container mt-1'>
                                                            <div className='fv-help-block'>
                                                                <span role='alert' className='text-danger'>
                                                                    {formik.errors.signerEmails[index] as any}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </div>

                                            <div className="col-md-5 col-12">
                                                <label htmlFor={`signerNames.${index}`} className={`fs-5 fw-semibold mt-3 ${theme === 'light' ? 'dark__text' : ''}`}>
                                                    Name {index + 1}
                                                </label>
                                                <input
                                                    type="text"
                                                    placeholder={`Enter Signer Name`}
                                                    name={`signerNames.${index}`}
                                                    className={clsx(
                                                        `form-control form-control-lg form-control-solid  ${theme === 'dark' ? 'dark__theme__input' : ''
                                                        }  italics-placeholder mt-2`,
                                                        {
                                                            'is-invalid': Array.isArray(formik.touched.signerNames) && formik.touched.signerNames[index] && Array.isArray(formik.errors.signerNames) && !!formik.errors.signerNames[index],
                                                        },
                                                        {
                                                            'is-valid': Array.isArray(formik.touched.signerNames) && formik.touched.signerNames[index] && Array.isArray(formik.errors.signerNames) && !formik.errors.signerNames[index],
                                                        }
                                                    )}

                                                    value={formik.values.signerNames[index] || ''}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />

                                            </div>

                                            {/* Remove Button */}
                                            <div className="col-md-2 d-flex align-items-end">
                                                <button
                                                    type="button"
                                                    className={`btn btn-danger w-100 ${Array.isArray(formik.touched.signerEmails) &&
                                                        Array.isArray(formik.errors.signerEmails) &&
                                                        !!formik.touched.signerEmails?.[index] &&
                                                        !!formik.errors.signerEmails?.[index]
                                                        ? 'mb-7'
                                                        : ''
                                                        }`}
                                                    onClick={() => {
                                                        const updatedEmails = [...formik.values.signerEmails];
                                                        const updatedNames = [...formik.values.signerNames];
                                                        updatedEmails.splice(index, 1);
                                                        updatedNames.splice(index, 1);
                                                        formik.setFieldValue('signerEmails', updatedEmails);
                                                        formik.setFieldValue('signerNames', updatedNames);
                                                    }}
                                                // onClick={removeSigner}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {Array.isArray(formik.touched.signerEmails) && typeof formik.errors.signerEmails === 'string' && (
                                    <div className="text-danger mb-3">{formik.errors.signerEmails}</div>
                                )}
                            </div>

                            <div className='col-lg-6 col-md-6 col-12 '>
                                <div className={`fs-5 fw-semibold mt-3 ${theme === 'light' ? 'dark__text' : ''}`}>
                                    {' '}
                                    Document Names<span className='text-danger'>*</span>
                                </div>
                                {/* </div> */}
                                <input
                                    placeholder='Document names (auto-generated from files)'
                                    autoComplete='off'
                                    type='text'
                                    value={formik.values.documentNames.join(', ')}
                                    readOnly
                                    className={clsx(
                                        `form-control form-control-lg form-control-solid  ${theme === 'dark' ? 'dark__theme__input' : ''
                                        }  italics-placeholder mt-2`,
                                        {
                                            'is-invalid': formik.touched.documentNames && formik.errors.documentNames,
                                        },
                                        {
                                            'is-valid': formik.touched.documentNames && !formik.errors.documentNames,
                                        }
                                    )}
                                />
                                {formik.touched.documentNames && formik.errors.documentNames && (
                                    <div className='fv-plugins-message-container mt-1'>
                                        <div className='fv-help-block'>
                                            <span role='alert' className='text-danger'>
                                                {typeof formik.errors.documentNames === 'string'
                                                    ? formik.errors.documentNames
                                                    : 'Document names are required'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className='col-lg-6 col-md-6 col-12 '>
                                <div className={`fs-5 fw-semibold mt-3 ${theme === 'light' ? 'dark__text' : ''}`}>
                                    {' '}
                                    Expiration Days<span className='text-danger'>*</span>
                                </div>
                                <input
                                    placeholder='Enter Subject'
                                    autoComplete='off'
                                    type='number'
                                    onWheel={(e) => e.currentTarget.blur()}
                                    min={1}
                                    {...formik.getFieldProps('expirationDays')}
                                    className={clsx(
                                        `form-control form-control-lg form-control-solid  ${theme === 'dark' ? 'dark__theme__input' : ''
                                        }  italics-placeholder mt-2`,
                                        {
                                            'is-invalid': formik.touched.expirationDays && formik.errors.expirationDays,
                                        },
                                        {
                                            'is-valid': formik.touched.expirationDays && !formik.errors.expirationDays,
                                        }
                                    )}
                                />
                                {formik.touched.expirationDays && formik.errors.expirationDays && (
                                    <div className='fv-plugins-message-container mt-1'>
                                        <div className='fv-help-block'>
                                            <span role='alert' className='text-danger'>
                                                {formik.errors.expirationDays as any}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className=' col-12 '>
                                <div className={`fs-5 fw-semibold mt-3 ${theme === 'light' ? 'dark__text' : ''}`}>
                                    {' '}
                                    Subject
                                </div>
                                <input
                                    placeholder='Enter Subject'
                                    autoComplete='off'
                                    type='text'
                                    {...formik.getFieldProps('subject')}
                                    className={clsx(
                                        `form-control form-control-lg form-control-solid  ${theme === 'dark' ? 'dark__theme__input' : ''
                                        }  italics-placeholder mt-2`,
                                        {
                                            'is-invalid': formik.touched.subject && formik.errors.subject,
                                        },
                                        {
                                            'is-valid': formik.touched.subject && !formik.errors.subject,
                                        }
                                    )}
                                />
                                {formik.touched.subject && formik.errors.subject && (
                                    <div className='fv-plugins-message-container mt-1'>
                                        <div className='fv-help-block'>
                                            <span role='alert' className='text-danger'>
                                                {formik.errors.subject as any}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className=' col-12 '>
                                <div className={`fs-5 fw-semibold mt-3 ${theme === 'light' ? 'dark__text' : ''}`}>
                                    {' '}
                                    Message
                                </div>
                                <textarea
                                    placeholder='Enter Message'
                                    autoComplete='off'
                                    rows={3}
                                    {...formik.getFieldProps('message')}
                                    className={clsx(
                                        `form-control form-control-lg form-control-solid  ${theme === 'dark' ? 'dark__theme__input' : ''
                                        }  italics-placeholder mt-2`,
                                        {
                                            'is-invalid': formik.touched.message && formik.errors.message,
                                        },
                                        {
                                            'is-valid': formik.touched.message && !formik.errors.message,
                                        }
                                    )}
                                />
                                {formik.touched.message && formik.errors.message && (
                                    <div className='fv-plugins-message-container mt-1'>
                                        <div className='fv-help-block'>
                                            <span role='alert' className='text-danger'>
                                                {formik.errors.message as any}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>

                        <div className=' d-flex justify-content-end pt-4'>
                            <button
                                type='submit'
                                className='btn btn-primary'
                                data-kt-users-modal-action='submit'
                                disabled={formik.isSubmitting}
                            >
                                Send Envelope
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal >
        </>
    )
}

export default SendEnvelopeModal